// src/core/score/gewerbeDealScore.ts
// Bruecke zwischen den Gewerbe-Mechanik-Kennzahlen (Zonen-Modell aus
// GewerbeCheck.tsx/gewerbeCalc.ts) und dem analyzer-uebergreifenden Deal-Score
// (dealScore.ts). Gleiches Muster wie etwDealScore.ts/mfhDealScore.ts.
//
// Hinweis: `tiUpfront` (Erstjahres-Mieterausbau, siehe GewerbeCheck.tsx
// `cashflowMonatY1 = (noiY1 - annuityYear - tiUpfront) / 12`) fliesst bewusst
// NICHT in noiJahr ein -- das ist eine einmalige Jahr-1-Investition, kein
// wiederkehrender Betriebsertrag, und die Deal-Score-Engine hat (wie bei den
// anderen 4 Typen) nur einen einzigen, wiederkehrenden NOI-Parameter fuer
// sowohl Rendite- als auch Cashflow-Saeule. Der Gewerbe-Analyzer selbst zeigt
// den TI-bereinigten Cashflow weiterhin zusaetzlich als eigene Kennzahl an.

import { computeDealScore, type DealScoreResult, type LageInput } from "./dealScore.js";
import { GEWERBE_SCORE_CONFIG, RENDITE_ZUSCHLAG_PP, BEWIRTSCHAFTUNG_PAUSCHALE_PCT } from "./dealScoreConfig.js";
import { resolveImportPcts, pctSum } from "../../lib/purchaseCosts.js";
import { computeFinancing, eigenkapitalFromLegacyLoan } from "../financing.js";
import { resolveBewirtschaftungModus } from "../bewirtschaftung.js";
import type { GewerbeSnapshotInput, Bonitaet } from "../../lib/gewerbeSnapshot.js";

/** Minimale Zonen-Form, die diese Datei fuer die Score-Berechnung braucht (strukturell kompatibel mit GewerbeZone aus gewerbeSnapshot.ts). */
export type GewerbeZoneLike = {
  areaM2: number;
  rentPerM2: number;
  vacancyPct: number;
  recoverablePct: number;
  freeRentMonthsY1: number;
  /** Phase-4 Runde 2b, Baustein 4 (Bankbericht aus der Akte): Mieterausbau (Jahr 1,
   *  einmalig) -- bisher nur lokal in GewerbeCheck.tsx (`zonenCalcY1.totalTI`), hier
   *  additiv ergaenzt, damit buildGewerbeBankberichtPayload() dieselbe Zahl ohne
   *  Parallel-Rechnung lesen kann. Fliesst bewusst NICHT in noiY1 ein (siehe Dateikopf). */
  tiPerM2: number;
};

const STRESS_ZINS_DELTA = 0.01;
const STRESS_MIETE_FAKTOR = 0.9;

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

export type GewerbeMetricsInput = {
  kaufpreis: number;
  priceAdjPct: number;
  applyAdjustments: boolean;
  nkPct: number;
  zonen: GewerbeZoneLike[];
  rentAdjPct: number;
  opexTotalPctBrutto: number;
  capexRuecklagePctBrutto: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5, Korrektur der frueheren "3 % uebernehmen"-
  // Vorgabe): optional, siehe MfhMetricsInput (core/metrics/mfh.ts) fuer das identische
  // Muster. Legacy-Zweig (kein Modus): opexTotalPctBrutto/capexRuecklagePctBrutto mit dem
  // bestehenden Zonen-recoverablePct-Modell, exakt wie bisher. Einzeln: nichtUmlagefaehigeKosten
  // ersetzt dort das recoverablePct-Modell durch einen direkten €/Jahr-Wert (Gewerbe hat
  // keinen gesetzlichen Instandhaltungs-Richtwert wie Wohnraum, § 28 II. BV gilt hier nicht --
  // deshalb kein Vorschlag-Button, nur das Einzeln-Feld selbst).
  bewirtschaftungModus?: "pauschal" | "einzeln";
  nichtUmlagefaehigeKosten?: number;
  verwaltung?: number;
  instandhaltungJeM2Jahr?: number;
  flaeche?: number;
  // Stress-Pass-Fix (18.10.2026, Dominiks Vorgabe): siehe MfhMetricsInput.pauschaleBasisMiete
  // (core/metrics/mfh.ts) -- identisches Muster, hier auf totalEff (effektive Jahresmiete
  // ueber alle Zonen) bezogen.
  pauschaleBasisTotalEff?: number;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026), wie MFH/EFH: Eigenkapital in Euro statt
   *  Beleihungsquote (LTV). */
  eigenkapital: number;
  zinsPct: number;
  tilgungPct: number;
};

export type GewerbeMetrics = {
  kaufpreisView: number;
  allIn: number;
  grossRentYearY1: number;
  /** Erzielte (Ist-)Jahresmiete nach Leerstand je Zone (vacancyPct) -- fuer Anzeigen, die
   *  die tatsaechliche statt der vollvermieteten Miete zeigen sollen (Nachtest-Korrektur
   *  12.10.2026, Punkt 1), z. B. die Bruttorendite-Kachel in /check. */
  effRentYearY1: number;
  /** Phase-4 Runde 2b, Baustein 4: Betriebskosten (nicht umlagefaehiger Teil) bzw.
   *  Instandhaltungsruecklage als eigene Posten -- vorher nur ihre Differenz zu noiY1
   *  sichtbar. Additiv, aendert noiY1 nicht. */
  landlordOpexY1: number;
  capexY1: number;
  /** Nur im Einzeln-Modus > 0 (Pauschal/Legacy: 0) -- fuer die Breakdown-Anzeige. */
  verwaltungEff: number;
  noiY1: number;
  loan: number;
  /** Bedarf (All-in) minus EK minus Kredit; 0, wenn das Eigenkapital die Nebenkosten deckt. */
  finanzierungsluecke: number;
  annuityYear: number;
  /** Phase-4 Runde 2b, Baustein 4 (Bankbericht aus der Akte): dieselben PDF-Detailfelder
   *  wie bisher nur lokal in GewerbeCheck.tsx -- additiv, aendert noiY1/Score nicht. */
  totalOpexY1: number;
  recoveredY1: number;
  tiUpfront: number;
  interestY1: number;
  principalY1: number;
};

export function computeZonesY1(zonen: GewerbeMetricsInput["zonen"], rentAdjPct: number) {
  let totalGross = 0, totalEff = 0, totalTI = 0;
  for (const z of zonen) {
    const grossY1 = z.areaM2 * z.rentPerM2 * (1 + rentAdjPct) * 12 * (1 - Math.min(z.freeRentMonthsY1, 12) / 12);
    const effY1 = grossY1 * (1 - clamp01(z.vacancyPct));
    totalGross += grossY1;
    totalEff += effY1;
    totalTI += z.areaM2 * Math.max(0, z.tiPerM2);
  }
  return { totalGross, totalEff, totalTI };
}

export function computeGewerbeMetrics(input: GewerbeMetricsInput): GewerbeMetrics {
  const adjustedPrice = Math.round(input.kaufpreis * (1 + input.priceAdjPct));
  const kaufpreisView = input.applyAdjustments ? adjustedPrice : input.kaufpreis;

  const { totalGross, totalEff, totalTI } = computeZonesY1(input.zonen, input.rentAdjPct);
  const totalOpexY1 = totalGross * input.opexTotalPctBrutto;
  const recoveredY1 = input.zonen.reduce((s, z) => {
    const grossZ = z.areaM2 * z.rentPerM2 * (1 + input.rentAdjPct) * 12 * (1 - Math.min(z.freeRentMonthsY1, 12) / 12);
    return s + grossZ * input.opexTotalPctBrutto * clamp01(z.recoverablePct);
  }, 0);

  // Bewirtschaftung Pauschal/Einzeln (Baustein 5) -- siehe Feldkommentar in GewerbeMetricsInput.
  let landlordOpexY1: number;
  let verwaltungEff: number;
  let capexY1: number;
  if (input.bewirtschaftungModus === "pauschal") {
    landlordOpexY1 = (input.pauschaleBasisTotalEff ?? totalEff) * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.gewerbe;
    verwaltungEff = 0;
    capexY1 = 0;
  } else if (input.bewirtschaftungModus === "einzeln") {
    landlordOpexY1 = Math.max(0, input.nichtUmlagefaehigeKosten ?? 0);
    verwaltungEff = Math.max(0, input.verwaltung ?? 0);
    capexY1 = Math.max(0, input.instandhaltungJeM2Jahr ?? 0) * Math.max(0, input.flaeche ?? 0);
  } else {
    // Legacy (kein Modus, z.B. unveraenderter freier Einstieg): exakt wie bisher, inkl. des
    // Zonen-recoverablePct-Modells.
    landlordOpexY1 = Math.max(0, totalOpexY1 - recoveredY1);
    verwaltungEff = 0;
    capexY1 = totalGross * input.capexRuecklagePctBrutto;
  }
  const noiY1 = totalEff - landlordOpexY1 - verwaltungEff - capexY1;

  const nkBetrag = Math.round(kaufpreisView * input.nkPct);
  const allIn = kaufpreisView + nkBetrag;

  // Finanzierung (Dominiks Vorgabe, 16.10.2026, einheitliche Darlehensregel -- wie MFH/EFH):
  // gemeinsame Funktion (core/financing.ts) statt einer eigenen Formel-Kopie.
  const financing = computeFinancing({ kaufpreis: kaufpreisView, nebenkosten: nkBetrag, eigenkapital: input.eigenkapital, financingOn: input.financingOn });
  const loan = financing.loan;
  const finanzierungsluecke = financing.finanzierungsluecke;
  // Flache Zins+Tilgung-Annuitaet (wie MFH/ETW/Gemischt), seit 14.10.2026 Runde 2 --
  // vorher eine exakt amortisierende Zins+Laufzeit-Annuitaet (annuityExact()), siehe
  // DEAL-FLOW-STATUS.md "Als Naechstes nach Runde 1".
  const annuityYear = input.financingOn ? loan * (input.zinsPct + input.tilgungPct) : 0;
  const interestY1 = input.financingOn ? loan * input.zinsPct : 0;
  const principalY1 = input.financingOn ? Math.max(0, annuityYear - interestY1) : 0;

  return {
    kaufpreisView, allIn, grossRentYearY1: totalGross, effRentYearY1: totalEff,
    landlordOpexY1, capexY1, verwaltungEff, noiY1, loan, finanzierungsluecke, annuityYear,
    totalOpexY1, recoveredY1, tiUpfront: totalTI, interestY1, principalY1,
  };
}

/** Migration (16.10.2026, einheitliche Darlehensregel): ein alter Gewerbe-Snapshot/-Export
 *  (vor der Umstellung von ltvPct auf eigenkapital) speicherte Kredit = Kaufpreis x ltvPct,
 *  ohne NK-Deckel. Aequivalentes Eigenkapital ueber die gemeinsame Migrations-Funktion
 *  (core/financing.ts). */
export function gewerbeEigenkapitalFromLegacyLtv(kaufpreis: number, ltvPct: number, nkPct: number): number {
  const nkBetrag = Math.round(kaufpreis * nkPct);
  const kreditAlt = kaufpreis * ltvPct;
  return eigenkapitalFromLegacyLoan(kaufpreis + nkBetrag, kreditAlt);
}

/** Loest das Eigenkapital aus einem (ggf. alten) Gewerbe-Snapshot-Input auf -- wendet
 *  gewerbeEigenkapitalFromLegacyLtv() an, wenn legacyLtvPct gesetzt ist, sonst der direkte
 *  Wert. */
export function resolveGewerbeEigenkapital(
  rawInput: { kaufpreis: number; eigenkapital: number; legacyLtvPct: number | null },
  nkPct: number
): number {
  return rawInput.legacyLtvPct == null
    ? rawInput.eigenkapital
    : gewerbeEigenkapitalFromLegacyLtv(rawInput.kaufpreis, rawInput.legacyLtvPct, nkPct);
}

/** Flaechengewichtete Durchschnitts-Restlaufzeit (WALT) ueber alle Zonen -- 0, wenn keine
 *  Zone eine Flaeche hat. Phase-4 Runde 2b, Baustein 4: aus GewerbeCheck.tsx extrahiert
 *  (dort bisher lokal/inline), hier geteilt fuer die Objektseite. */
export function avgWeighted(items: { w: number; v: number }[]): number {
  const w = items.reduce((s, i) => s + i.w, 0);
  if (w <= 0) return 0;
  return items.reduce((s, i) => s + i.v * i.w, 0) / w;
}

/** Zuschlag/Abschlag auf die angenommene Markt-Cap-Rate je nach Restlaufzeit (WALT),
 *  Mieter-Bonitaet und Indexierung -- aus GewerbeCheck.tsx extrahiert (siehe dort
 *  `calcCapSpread()`, unveraendert uebernommen), geclampt auf [-0,4 %; +1,2 %]. */
export function calcGewerbeCapSpread(walt: number, bonitaet: Bonitaet, indexiert: boolean): number {
  const spreadWalt = walt < 3 ? 0.006 : walt < 5 ? 0.003 : walt < 8 ? 0.0 : -0.002;
  const spreadBon = bonitaet === "AAA" ? -0.003 : bonitaet === "A" ? -0.002 : bonitaet === "B" ? 0 : 0.004;
  const spreadIdx = indexiert ? -0.001 : 0;
  return Math.max(-0.004, Math.min(0.012, spreadWalt + spreadBon + spreadIdx));
}

/** "Wert vs. Kaufpreis" fuer Gewerbe (Dominiks Vorgabe, Baustein 4, Teil 2 Punkt b):
 *  Modellwert aus NOI/effektive-Cap-Rate, dieselbe Formel wie GewerbeCheck.tsx
 *  (`wertAusCap`/`capEff`), hier geteilt statt dupliziert, damit die Objektseite
 *  garantiert denselben Wert zeigt. */
export function computeGewerbeWertVsKaufpreis(
  noiY1: number,
  zonen: { areaM2: number; leaseTermYears: number }[],
  bonitaetTop3: Bonitaet,
  indexiert: boolean,
  capRateAssumed: number
): { wert: number; capRateObjekt: number; capSpreadPct: number } {
  const avgWalt = avgWeighted(zonen.map((z) => ({ w: z.areaM2, v: z.leaseTermYears })));
  const capSpreadPct = calcGewerbeCapSpread(avgWalt, bonitaetTop3, indexiert);
  const capRateObjekt = Math.max(0.0001, capRateAssumed + capSpreadPct);
  const wert = capRateObjekt > 0 ? noiY1 / capRateObjekt : 0;
  return { wert, capRateObjekt, capSpreadPct };
}

/** Gesamtflaeche ueber alle Zonen -- fuer die Instandhaltungs-€/m²-Umrechnung. */
export function gewerbeFlaecheFromZonen(zonen: { areaM2: number }[]): number {
  return zonen.reduce((s, z) => s + z.areaM2, 0);
}

/** Legacy landlordOpexY1/capexY1 (Zonen-recoverablePct-Modell) aus den rohen Snapshot-
 *  Feldern -- Eingangswert fuer resolveGewerbeBewirtschaftung()' Migrationsumrechnung. */
export function gewerbeLegacyBewirtschaftungY1(
  zonen: GewerbeZoneLike[],
  rentAdjPct: number,
  opexTotalPctBrutto: number,
  capexRuecklagePctBrutto: number
): { landlordOpexY1: number; capexY1: number; totalEff: number } {
  const { totalGross, totalEff } = computeZonesY1(zonen, rentAdjPct);
  const totalOpexY1 = totalGross * opexTotalPctBrutto;
  const recoveredY1 = zonen.reduce((s, z) => {
    const grossZ = z.areaM2 * z.rentPerM2 * (1 + rentAdjPct) * 12 * (1 - Math.min(z.freeRentMonthsY1, 12) / 12);
    return s + grossZ * opexTotalPctBrutto * clamp01(z.recoverablePct);
  }, 0);
  return { landlordOpexY1: Math.max(0, totalOpexY1 - recoveredY1), capexY1: totalGross * capexRuecklagePctBrutto, totalEff };
}

/**
 * Migrations-Bruecke Bewirtschaftung Pauschal/Einzeln (Baustein 5), NACHKORRIGIERT
 * 17.10.2026 -- identisches Muster wie resolveMfhBewirtschaftung()/resolveEfhBewirtschaftung().
 * Siehe core/bewirtschaftung.ts' resolveBewirtschaftungModus() fuer die Regel: "Pauschal" nur,
 * wenn das bisherige Zonen-recoverablePct-Modell (legacyLandlordOpexY1) der Pauschale (auf
 * effektive Jahresmiete) exakt entspricht (Toleranz 1 €) UND capexY1 0 ist -- sonst "Einzeln"
 * mit dem historischen €-Betrag 1:1 uebernommen (kein Modell-Wechsel, Score bleibt unveraendert).
 */
export function resolveGewerbeBewirtschaftung(
  rawInput: { bewirtschaftungModus?: unknown; capexRuecklagePctBrutto: number; nichtUmlagefaehigeKosten?: number; verwaltung?: number; instandhaltungJeM2Jahr?: number },
  legacyLandlordOpexY1: number,
  legacyCapexY1: number,
  legacyTotalEff: number,
  flaeche: number
): { bewirtschaftungModus: "pauschal" | "einzeln"; nichtUmlagefaehigeKosten: number; verwaltung: number; instandhaltungJeM2Jahr: number; migriertVonAlt: boolean; instandhaltungVermutlichEnthalten: boolean } {
  const pauschaleBetrag = legacyTotalEff * BEWIRTSCHAFTUNG_PAUSCHALE_PCT.gewerbe;
  const { modus, migriertVonAlt } = resolveBewirtschaftungModus(
    rawInput.bewirtschaftungModus, rawInput.capexRuecklagePctBrutto, legacyLandlordOpexY1, pauschaleBetrag
  );
  if (modus === "pauschal") {
    return { bewirtschaftungModus: "pauschal", nichtUmlagefaehigeKosten: 0, verwaltung: 0, instandhaltungJeM2Jahr: 0, migriertVonAlt: false, instandhaltungVermutlichEnthalten: false };
  }
  let nichtUmlagefaehigeKosten: number, verwaltung: number, instandhaltungJeM2Jahr: number;
  if (migriertVonAlt) {
    nichtUmlagefaehigeKosten = legacyLandlordOpexY1;
    verwaltung = 0;
    instandhaltungJeM2Jahr = flaeche > 0 ? legacyCapexY1 / flaeche : 0;
  } else {
    nichtUmlagefaehigeKosten = rawInput.nichtUmlagefaehigeKosten ?? 0;
    verwaltung = rawInput.verwaltung ?? 0;
    instandhaltungJeM2Jahr = rawInput.instandhaltungJeM2Jahr ?? 0;
  }
  const instandhaltungVermutlichEnthalten = instandhaltungJeM2Jahr === 0 && nichtUmlagefaehigeKosten >= pauschaleBetrag;
  return { bewirtschaftungModus: "einzeln", nichtUmlagefaehigeKosten, verwaltung, instandhaltungJeM2Jahr, migriertVonAlt, instandhaltungVermutlichEnthalten };
}

function toDealScoreInput(
  metrics: GewerbeMetrics,
  stressMetrics: GewerbeMetrics,
  financingOn: boolean,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein"
) {
  return {
    kaufpreis: metrics.kaufpreisView,
    gesamtinvestition: metrics.allIn,
    jahreskaltmiete: metrics.grossRentYearY1,
    noiJahr: metrics.noiY1,
    annuitaetJahr: metrics.annuityYear,
    financingOn,
    stressNoiJahr: stressMetrics.noiY1,
    stressAnnuitaetJahr: stressMetrics.annuityYear,
    lage,
    renditeZuschlagPp: RENDITE_ZUSCHLAG_PP.gewerbe,
    vermietungsstatus,
  };
}

/** Fuer die lebende Analyzer-Seite: metrics ist das Ergebnis eines bereits erfolgten computeGewerbeMetrics()-Aufrufs. */
export function computeGewerbeDealScoreFromMetrics(
  baseInput: GewerbeMetricsInput,
  metrics: GewerbeMetrics,
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const stressInput: GewerbeMetricsInput = {
    ...baseInput,
    zinsPct: baseInput.zinsPct + STRESS_ZINS_DELTA,
    zonen: baseInput.zonen.map((z) => ({ ...z, rentPerM2: z.rentPerM2 * STRESS_MIETE_FAKTOR })),
    // Stress-Pass-Fix (18.10.2026): Pauschal auf Basis-Miete fix, siehe mfhDealScore.ts.
    pauschaleBasisTotalEff: metrics.effRentYearY1,
  };
  const stressMetrics = computeGewerbeMetrics(stressInput);
  return computeDealScore(
    toDealScoreInput(metrics, stressMetrics, baseInput.financingOn, lage, vermietungsstatus),
    GEWERBE_SCORE_CONFIG
  );
}

/** Fuer "Meine Deals"/PDF: nur die gespeicherten Rohdaten vorhanden, kein live computeGewerbeMetrics()-Ergebnis. */
export function computeGewerbeDealScoreFromSnapshotInput(
  rawInput: GewerbeSnapshotInput & Record<string, unknown>,
  playground: { priceAdjPct: number; rentAdjPct: number; applyAdjustments: boolean },
  lage: LageInput,
  vermietungsstatus: "vollstaendig" | "teilweise" | "nein" = "vollstaendig"
): DealScoreResult {
  const nkPct = pctSum(resolveImportPcts(rawInput, "gewerbe"));
  const flaeche = gewerbeFlaecheFromZonen(rawInput.zonen);
  const legacy = gewerbeLegacyBewirtschaftungY1(rawInput.zonen, playground.rentAdjPct, rawInput.opexTotalPctBrutto, rawInput.capexRuecklagePctBrutto);
  const bewirtschaftung = resolveGewerbeBewirtschaftung(rawInput, legacy.landlordOpexY1, legacy.capexY1, legacy.totalEff, flaeche);
  const base: GewerbeMetricsInput = {
    kaufpreis: rawInput.kaufpreis,
    priceAdjPct: playground.priceAdjPct,
    applyAdjustments: playground.applyAdjustments,
    nkPct,
    zonen: rawInput.zonen,
    rentAdjPct: playground.rentAdjPct,
    opexTotalPctBrutto: rawInput.opexTotalPctBrutto,
    capexRuecklagePctBrutto: rawInput.capexRuecklagePctBrutto,
    bewirtschaftungModus: bewirtschaftung.bewirtschaftungModus, nichtUmlagefaehigeKosten: bewirtschaftung.nichtUmlagefaehigeKosten,
    verwaltung: bewirtschaftung.verwaltung, instandhaltungJeM2Jahr: bewirtschaftung.instandhaltungJeM2Jahr, flaeche,
    financingOn: rawInput.financingOn,
    eigenkapital: resolveGewerbeEigenkapital(rawInput as any, nkPct),
    zinsPct: rawInput.zinsPct,
    tilgungPct: rawInput.tilgungPct,
  };
  const metrics = computeGewerbeMetrics(base);
  return computeGewerbeDealScoreFromMetrics(base, metrics, lage, vermietungsstatus);
}
